<template>
    <div class="modal-container">
        <div class="modal-header">
            <h4 class="modal-title">{{ isUpdate ? 'Update' : 'Create' }} Service</h4>
            <md-button class="md-simple md-just-icon md-round modal-default-button" @click.stop="$modal.hide">
                <md-icon>clear</md-icon>
            </md-button>
        </div>
        <div class="modal-body">
            <form-wrapper :validator="$v.serviceDetails" class="form-wrapper">
                <div class="form-container service-form-container">
                    <div class="service-details">
                        <form-group name="serviceCode" label="Code" attribute="Service Code" :messages="localMessages">
                            <md-input v-focus v-model="serviceDetails.serviceCode" />
                        </form-group>

                        <form-group
                            name="name"
                            label="Description"
                            attribute="Service Description"
                            :messages="localMessages"
                        >
                            <md-input v-model="serviceDetails.name" required />
                        </form-group>

                        <form-group
                            name="accountCode"
                            label="Account Code"
                            attribute="Account Code"
                            :messages="localMessages"
                        >
                            <md-input v-model="serviceDetails.accountCode" />
                        </form-group>

                        <form-group
                            name="quantityType"
                            label="Quantity Type"
                            attribute="Quantity Type"
                            :messages="localMessages"
                        >
                            <md-input v-model="serviceDetails.quantityType" />
                        </form-group>

                        <form-group
                            name="unitPriceExTax"
                            label="Unit Price (Ex. Tax)"
                            attribute="Unit Price"
                            :messages="localMessages"
                        >
                            <md-input v-model="serviceDetails.unitPriceExTax" />
                        </form-group>

                        <form-group
                            name="unitCostExTax"
                            label="Unit Cost (Ex. Tax)"
                            attribute="Unit Cost"
                            :messages="localMessages"
                        >
                            <md-input v-model="serviceDetails.unitCostExTax" />
                        </form-group>
                    </div>
                </div>
            </form-wrapper>
        </div>
        <div class="modal-footer">
            <md-button class="dialog-button md-primary" @click="validateService">
                {{ isUpdate ? 'Update' : 'Create' }}
            </md-button>
            <md-button class="dialog-button md-default" @click.stop="$modal.hide">Cancel</md-button>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { isEmptyOrSpaces, handleRequests, showErrorMessage } from '@/helpers';
import { required, maxLength, decimal } from 'vuelidate/lib/validators';
import { GeneralMixin } from '@/mixins/GeneralMixin';

export default {
    name: 'CreateServiceModal',
    mixins: [GeneralMixin],
    props: {
        serviceDetails: {
            type: Object,
            default: () => ({
                serviceRates: [
                    {
                        unitPriceExTax: 0,
                        unitCostExTax: 0
                    }
                ]
            })
        },
        isUpdate: {
            type: Boolean,
            default: false
        },
        teamRegionId: {
            type: Number,
            default: () => {}
        },
        resolve: {
            type: Function,
            default: () => {}
        }
    },
    validations: {
        serviceDetails: {
            serviceCode: {
                maxLength: maxLength(200)
            },
            name: { required, maxLength: maxLength(200) },
            accountCode: {
                maxLength: maxLength(15)
            },
            unitPriceExTax: {
                decimal,
                minValue: (value) => !value || value >= 0,
                maxValue: (value) => !value || value < 100000000
            },
            priceCurrency: {},
            unitCostExTax: {
                decimal,
                minValue: (value) => !value || value >= 0,
                maxValue: (value) => !value || value < 100000000
            },
            costCurrency: {},
            quantityType: { maxLength: maxLength(10) }
        }
    },
    data() {
        return {
            localMessages: {
                required: '{attribute} is required.',
                decimal: '{attribute} should contain a decimal number.',
                minValue: '{attribute} should have a positive value.',
                maxValue: '{attribute} should be less than 100000000.'
            }
        };
    },
    computed: {
        ...mapGetters({
            user: 'user/user'
        })
    },
    mounted() {},
    watch: {},
    methods: {
        async create() {
            try {
                this.$_handleLoaderState(true, 'PROCESSING...');
                const api = `/api/services?teamRegionId=${this.teamRegionId}`;

                const payload = {
                    method: 'post',
                    data: this.serviceDetails
                };

                const response = await handleRequests(api, payload);
                if (response.status === 201) {
                    this.$notify({
                        message: 'Successfully created service.',
                        type: 'success'
                    });

                    this.$v.$reset();
                    this.resolve('ok');
                }
            } catch (error) {
                const message = 'Error in creating the service';
                showErrorMessage(this, message, error);
            }
            this.$_handleLoaderState(false);
        },
        async updateServiceDetails() {
            try {
                this.$_handleLoaderState(true, 'UPDATING...');

                const api = `/api/services/${this.serviceDetails.serviceId}?teamRegionId=${this.teamRegionId}`;
                const payload = {
                    method: 'put',
                    data: this.serviceDetails
                };

                await handleRequests(api, payload);

                this.$notify({
                    message: 'Successfully updated service.',
                    type: 'success'
                });

                this.$v.$reset();
                this.resolve('ok');
            } catch (error) {
                const message = 'Error in updating the service.';
                showErrorMessage(this, message, error);
            }
            this.$_handleLoaderState(false);
        },
        fixFloats(obj) {
            obj.unitPriceExTax = parseFloat(obj.unitPriceExTax);
            if (Number.isNaN(obj.unitPriceExTax)) 
                obj.unitPriceExTax = null;
            obj.unitCostExTax = parseFloat(obj.unitCostExTax);
            if (Number.isNaN(obj.unitCostExTax)) 
                obj.unitCostExTax = null;
        },
        validateService() {
            this.$v.serviceDetails.$touch();

            if (!this.$v.serviceDetails.$invalid) {
                if (!isEmptyOrSpaces(this.serviceDetails.name)) {
                    this.serviceDetails.name = this.serviceDetails.name.trim();
                }

                // Trim service code if provided
                if (this.serviceDetails.serviceCode && !isEmptyOrSpaces(this.serviceDetails.serviceCode)) {
                    this.serviceDetails.serviceCode = this.serviceDetails.serviceCode.trim();
                }

                // Trim account code if provided
                if (this.serviceDetails.accountCode && !isEmptyOrSpaces(this.serviceDetails.accountCode)) {
                    this.serviceDetails.accountCode = this.serviceDetails.accountCode.trim();
                }

                this.fixFloats(this.serviceDetails);

                if (this.isUpdate) {
                    this.updateServiceDetails();
                } else {
                    this.create();
                }

                return;
            }

            this.$notify({
                message: 'There are errors in the form. Please correct them to continue.',
                type: 'danger'
            });
        }
    }
};
</script>

<style lang="scss" scoped>
::v-deep .md-menu-content.md-select-menu {
    width: 150px;
}
.modal-body {
    padding-top: 12px;
}
.modal-container {
    max-width: 600px;
}
.title {
    margin-bottom: 12px;
    text-align: center;
}

/*.form-container {
    height: 500px;
}*/
</style>
